import React, { useCallback, useEffect, useState } from "react";
import { FileText, BellRing, Loader2, History } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Request failed"; };
const yesterday = () => new Date(Date.now() - 864e5).toISOString().slice(0, 10);
const RUN_CLS = { SUCCESS: "text-emerald-700", FAILED: "text-rose-700 font-bold", RUNNING: "text-amber-700" };

function Section({ id, title, rows, render }) {
  return (
    <details className="rounded-lg bg-stone-50 p-2" data-testid={`mod-report-${id}`}>
      <summary className="cursor-pointer font-semibold">{title} <span className="text-stone-500">({rows.length})</span></summary>
      {rows.length === 0 ? <p className="text-stone-500 mt-1">None.</p> :
        <ul className="mt-1 space-y-0.5">{rows.map((r, i) => <li key={i} className="font-mono text-[11px] break-all">{render(r)}</li>)}</ul>}
    </details>
  );
}

function ReportBody({ r }) {
  return (
    <div className="space-y-2 mt-2" data-testid="mod-daily-latest">
      <p>{r.report_date}: {r.new_cases} new · {r.urgent} urgent · {r.unresolved} unresolved · {r.overdue} overdue · {r.false_positives} false positives · {r.appeals_pending} appeals · generated {r.generated_at?.slice(0, 16)} by {r.generated_by}</p>
      <Section id="actions" title="Actionable review items" rows={r.action_items || []} render={(x) => `[${x.priority}] ${x.type} ${x.ref} — ${x.action}`} />
      <Section id="flagged" title="Flagged content (open)" rows={r.flagged_content || []} render={(x) => `${x.case_id} · ${x.content_type} · ${x.classification} · ${x.severity}`} />
      <Section id="unresolved" title="Unresolved cases" rows={r.unresolved_cases || []} render={(x) => `${x.case_id} · ${x.status} · due ${x.due_at?.slice(0, 16)}`} />
      <Section id="duplicates" title="Duplicate listing candidates" rows={r.duplicate_candidates || []} render={(x) => `${x.count}× “${x.title}” ${x.city || ""}${x.same_owner ? " · same owner" : ""} · ${x.listing_ids.join(", ")}`} />
      <Section id="prices" title="Suspicious prices" rows={r.suspicious_prices || []} render={(x) => `${x.listing_id} “${x.title}” · ${x.price} vs median ${x.category_median ?? "n/a"} · ${x.reason}`} />
      {r.insight_method && <p className="text-[10px] text-stone-500">Method: {r.insight_method}</p>}
    </div>
  );
}

function RunHistory({ runs, sched }) {
  return (
    <div className="text-[11px]" data-testid="mod-run-history">
      <div className="flex items-center gap-1 font-semibold"><History className="w-3 h-3" />Scheduler &amp; run history</div>
      {sched && Object.entries(sched).map(([k, v]) => (
        <p key={k} data-testid={`mod-sched-${k}`}>{k}: last run {v.last_run ? `${v.last_run.status} (${v.last_run.trigger}, ${v.last_run.started_at?.slice(0, 16)})` : "never"} · last scheduled success {v.last_scheduled_success_at?.slice(0, 16) || "never"}</p>))}
      <ul className="mt-1 max-h-40 overflow-auto">{runs.map((x) => (
        <li key={x.run_id} data-testid={`mod-run-${x.run_id}`}><span className={RUN_CLS[x.status]}>{x.status}</span> · {x.kind} · {x.trigger} · {x.started_at?.slice(0, 16)} · attempts {x.attempts?.length}{x.error ? ` · ${x.error}` : ""}</li>))}</ul>
    </div>
  );
}

export function ModerationReportPanel() {
  const [items, setItems] = useState([]);
  const [runs, setRuns] = useState({ items: [], scheduler: null });
  const [delivery, setDelivery] = useState(null);
  const [day, setDay] = useState(yesterday());
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/admin/moderation/reports/daily").then((r) => setItems(r.data.items)).catch(() => {});
    api.get("/admin/moderation/runs?limit=15").then((r) => setRuns(r.data)).catch(() => {});
    api.get("/admin/moderation/delivery-status").then((r) => setDelivery(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "report") { const r = await api.post("/admin/moderation/reports/daily", { report_date: day }); toast.success(r.data.duplicate ? `Report for ${day} already exists — showing stored copy` : `Report for ${day} generated`); }
      else { const r = await api.post("/admin/moderation/reminders/run"); toast.success(`Reminders: ${r.data.result?.sent ?? 0} sent, ${r.data.result?.deduped ?? 0} already sent today`); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(""); load();
  };
  const r = items.find((x) => x.report_date === day) || items[0];
  return (
    <div className="rounded-xl border border-stone-200 p-3 text-xs space-y-2" data-testid="mod-daily-report">
      <div className="flex flex-wrap items-center gap-2"><FileText className="w-4 h-4" /><b>Daily moderation report</b>
        <input type="date" data-testid="mod-daily-date" value={day} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDay(e.target.value)} className="ml-auto border border-stone-200 rounded px-2 py-0.5" />
        <button data-testid="mod-daily-generate" disabled={!!busy} onClick={() => act("report")} className="px-3 py-1 rounded-full bg-stone-900 text-white disabled:opacity-50">{busy === "report" ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Generate report"}</button>
        <button data-testid="mod-reminders-run" disabled={!!busy} onClick={() => act("reminders")} className="px-3 py-1 rounded-full bg-stone-100 disabled:opacity-50"><BellRing className="w-3 h-3 inline" /> Send overdue reminders</button>
      </div>
      {r ? <ReportBody r={r} /> : <p className="text-stone-500" data-testid="mod-daily-empty">No reports yet. Auto-generated daily at 08:00 IST.</p>}
      {delivery && <p className="text-[11px]" data-testid="mod-delivery-status">Delivery: dashboard + in-app notifications <b className="text-emerald-700">connected</b> ({delivery.dashboard.notifications_to_you} to you) · ChatGPT <b className="text-stone-600">{delivery.chatgpt.status}</b> · external {delivery.external.status}</p>}
      <RunHistory runs={runs.items} sched={runs.scheduler} />
    </div>
  );
}
